import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import moment from 'moment';

import LineTitle from '../atoms/titles/LineTitle';
import LinkButton from '../atoms/button/LinkButton';
import SelectBox from '../atoms/form/SelectBox';
import Modal from '../atoms/modal/Modal';
import CheckBox from '../atoms/form/CheckBox';
import {
  setStayNights,
  setNumberOfRooms,
  setPeoplePerRoom,
  setRoomType,
  setMealCondition,
  setTagId,
  setDirectionId,
  setPrefectureId,
  setAreaId,
  setDistrictId,
  setKeyword,
  setSearchCondition,
  setSearchConditions,
  setExcludeLandingFlag,
  setGroupId,
  setItemCD,
  setHotelCode,
} from '../../modules/hotel';
import { createOptions, convertEnumValuesToOptions, addUnselectedOption } from '../../utils/search';
import { FetchDirections, FetchRoomTypes, FetchMealConditions, FetchTaggroups, FetchSearchConditions } from '../../queries/index.graphql';
import { useRoomAllocation } from '../../utils/roomAllocation';
import { DEFAULT_UNSELECETD_LABEL, ENABLE_CHANGE_STAY_NIGHTS_FEATURE, UNSELECTED_LABEL, PLAN_LIST_HIDE_TAGS } from '../../constants';

const selector = (state) => ({
  hotel: state.hotel,
  flight: state.flight,
});

const ModalAccommodationConditions = (props) => {
  const {
    open,
    onClose,
    onCancel,
    onClick,
    handleSearch,
    hideRoom,
    hideDestination,
    maxStayNights,
    hideSearchCondition,
    excludeLanding,
    isPlanList,
  } = props;

  const [ isHide, setIsHide ] = useState(true);
  const { hotel, flight } = useSelector(selector);
  const { hotelsInput, queryParamsValidated: queryParamsValidatedHotel } = hotel;
  const {
    stayNights,
    numberOfRooms,
    peoplePerRoom,
    directionId,
    prefectureId,
    areaId,
    districtId,
    roomType,
    mealCondition,
    tagIds,
    keyword,
    // sortOrder,
    searchCondition,
  } = hotelsInput;

  const dispatch = useDispatch();

  const { data } = useQuery(FetchDirections);
  const directions = data ? data.directions : [];
  const { data: roomTypesData } = useQuery(FetchRoomTypes);
  const roomTypes = roomTypesData ? convertEnumValuesToOptions(roomTypesData.__type.enumValues) : [];
  const { data: mealConditionsData } = useQuery(FetchMealConditions);
  const mealConditions = mealConditionsData ? convertEnumValuesToOptions(mealConditionsData.__type.enumValues) : [];
  const { data: taggroupsData } = useQuery(FetchTaggroups);
  const { data: searchConditionsData } = useQuery(FetchSearchConditions);
  let taggroups =  taggroupsData && taggroupsData.taggroups;
  let planConditions =  [];
  let otherConditions =  [];
  if (taggroups){
    //プラン一覧画面で、且つ追加宿泊でない場合は、こだわり条件の選択肢から施設関連のタグを除く
    if(isPlanList && !excludeLanding){
      taggroups = taggroups.filter(tg => !PLAN_LIST_HIDE_TAGS.includes(tg.name));
    }
    planConditions = taggroups.filter((tg) => tg.name === 'プラン');
    otherConditions = taggroups.filter((tg) => tg.name !== 'プラン');
  }
  const searchConditionsList = searchConditionsData ? searchConditionsData.searchConditions : [];
  
  const direction = directions.find((d) => d.id === directionId);
  const prefectures = direction ? direction.prefectures : [];
  const prefecturesOptions = addUnselectedOption(prefectures, UNSELECTED_LABEL);
  const prefecture = prefectures.find((p) => p.id && p.id === prefectureId);
  const areas = prefecture ? addUnselectedOption(prefecture.areas, UNSELECTED_LABEL) : addUnselectedOption([]);
  const area = areas.find((a) => a.id && a.id === areaId);
  const districts = area ? addUnselectedOption(area.districts, UNSELECTED_LABEL) : addUnselectedOption([]);

  const handleChangeStayNights = useCallback((e) => {
    dispatch(
      setStayNights(Number(e.target.value))
    );
  }, []);

  const handleChangeNumberOfRooms = useCallback((e) => {
    dispatch(
      setNumberOfRooms(Number(e.target.value))
    );
  }, []);

  const handleChangeRoomType = useCallback((e) => {
    const value = e.target.value !== DEFAULT_UNSELECETD_LABEL ? e.target.value : undefined;
    dispatch(
      setRoomType(value)
    );
  }, []);

  const handleChangeMealCondition = useCallback((e) => {
    dispatch(
      setMealCondition(e.target.value)
    );
  }, []);

  const handleChangeKeyword = useCallback((e) => {
    dispatch(
      setKeyword(e.target.value)
    );
  }, []);

  const handleChangeDirection = useCallback((e) => {
    const newDirectionId = e.target.value;
    // const newDirection = directions.find((d) => d.id === newDirectionId);
    // const newPrefectureId = newDirection.prefectures ? newDirection.prefectures[0].id : undefined;
    const newPrefectureId = undefined;
    dispatch(
      setDirectionId(newDirectionId)
    );
    dispatch(
      setPrefectureId(newPrefectureId)
    );
    dispatch(
      setAreaId(undefined)
    );
    dispatch(
      setDistrictId(undefined)
    );
  }, [directions]);

  const handleChangePrefecture = useCallback((e) => {
    const value = e.target.value !== UNSELECTED_LABEL ? e.target.value : undefined;
    dispatch(
      setPrefectureId(value)
    );
    dispatch(
      setAreaId(undefined)
    );
    dispatch(
      setDistrictId(undefined)
    );
  }, []);

  const handleChangeArea = useCallback((e) => {
    const value = e.target.value !== UNSELECTED_LABEL ? e.target.value : undefined;
    dispatch(
      setAreaId(value)
    );
    dispatch(
      setDistrictId(undefined)
    );
  }, []);

  const handleChangeDistrict = useCallback((e) => {
    const value = e.target.value !== UNSELECTED_LABEL ? e.target.value : undefined;
    dispatch(
      setDistrictId(value)
    );
  }, []);

  const { flightsInput, queryParamsValidated: queryParamsValidatedFlight } = flight;
  const departureDate = moment(flightsInput.outbound.date).format('YYYY年MM月DD日(ddd)');
  const arrivalDate = moment(flightsInput.inbound.date).format('YYYY年MM月DD日(ddd)');
  const period = `${departureDate} 〜 ${arrivalDate}`;

  const { numberOfRoomsOptions, peoplePerRoom: calculatedPeoplePerRoom } = useRoomAllocation(flightsInput, numberOfRooms);

  useEffect(() => {
    if (excludeLanding) {
      dispatch(setExcludeLandingFlag(1));
      dispatch(setSearchConditions([]));
      dispatch(setGroupId(''));
      dispatch(setItemCD(''));
      dispatch(setHotelCode(''));
    }
  }, [excludeLanding]);

  useEffect(() => {
    if (hideSearchCondition) {
      dispatch(setSearchConditions([]));
    }
  }, [hideSearchCondition]);

  useEffect(() => {
    if (!(queryParamsValidatedFlight && queryParamsValidatedHotel)) {
      return;
    }

    const validNumberOfRooms = numberOfRoomsOptions.find((option) => option.value === numberOfRooms);
    if (!validNumberOfRooms && numberOfRoomsOptions && numberOfRoomsOptions.length) {
      dispatch(
        setNumberOfRooms(Number(numberOfRoomsOptions[0].value))
      );
    }
  }, [numberOfRoomsOptions, numberOfRooms, queryParamsValidatedFlight, queryParamsValidatedHotel]);

  useEffect(() => {
    if (!(queryParamsValidatedFlight && queryParamsValidatedHotel)) {
      return;
    }

    if (peoplePerRoom !== calculatedPeoplePerRoom) {
      dispatch(
        setPeoplePerRoom(Number(calculatedPeoplePerRoom))
      );
    }
  }, [calculatedPeoplePerRoom, queryParamsValidatedFlight, queryParamsValidatedHotel]);

  const stayNightsOptions = useMemo(() => createOptions(1, maxStayNights || 7, '泊'), [maxStayNights]);

  return (
    <Modal
      title={'宿泊条件を変更する'}
      open={open}
      onClose={onClose}
      onCancel={onCancel}
      onClick={onClick}
      className="fullHeight"
      overlayClassName="modalAccommodationConditionsOverlay"
    >
      <div className="searchModal">
        <div className="searchModalContent">
          <LineTitle text="宿泊条件">
            <span style={{ marginLeft: 15, fontSize: 14 }}>{period}</span>
          </LineTitle>
          <div className="searchModalForm">
            <div className="searchModalFormItem">
              <div className="searchModalFormTitle">泊数</div>
              <div className="searchModalFormInput">
                {ENABLE_CHANGE_STAY_NIGHTS_FEATURE ? (
                  <div className="selectWrap">
                    <SelectBox
                      options={stayNightsOptions}
                      value={stayNights}
                      onChange={handleChangeStayNights}
                    />
                  </div>
                ) : (
                  <div style={{ fontWeight: 'bold' }}>{stayNights}泊</div>
                )}
              </div>
            </div>
            {!hideRoom && (
              <div className="searchModalFormItem alignTop">
                <div className="searchModalFormTitle">部屋数</div>
                <div className="searchModalFormInput">
                  <div className="selectWrap"><span className="selectWrapTitleLong"/>
                    <SelectBox
                      options={numberOfRoomsOptions}
                      value={numberOfRooms}
                      onChange={handleChangeNumberOfRooms}
                    />
                  </div>
                  <div className="selectWrap">
                    <span className="selectWrapTitleLong">利用人数</span>
                    <div style={{ fontWeight: 'bold', paddingLeft: 10 }}>{peoplePerRoom}名</div>
                  </div>
                </div>
              </div>
            )}
            {!hideDestination && (
              <div className="searchModalFormItem alignTop">
                <div className="searchModalFormTitle">目的地</div>
                <div className="searchModalFormInput">
                  <div className="selectWrap">
                    <span className="selectWrapTitleLong">方面</span>
                    <SelectBox
                      options={directions}
                      value={directionId}
                      onChange={handleChangeDirection}
                    />
                  </div>
                  <div className="selectWrap">
                    <span className="selectWrapTitleLong">都道府県</span>
                    <SelectBox
                      options={prefecturesOptions}
                      value={prefectureId}
                      onChange={handleChangePrefecture}
                    />
                  </div>
                  <div className="selectWrap">
                    <span className="selectWrapTitleLong">エリア</span>
                    <SelectBox
                      options={areas}
                      value={areaId}
                      onChange={handleChangeArea}
                    />
                  </div>
                  <div className="selectWrap">
                    <span className="selectWrapTitleLong">地区</span>
                    <SelectBox
                      options={districts}
                      value={districtId}
                      onChange={handleChangeDistrict}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="searchModalFormItem vertical">
              <div className="searchModalFormTitle">部屋タイプ</div>
              <div className="searchModalFormInput">
                <SelectBox
                  options={roomTypes}
                  value={roomType}
                  onChange={handleChangeRoomType}
                  size="lg"
                />
              </div>
            </div>
            <div className="searchModalFormItem vertical">
              <div className="searchModalFormTitle">食事条件</div>
              <div className="searchModalFormInput">
                <SelectBox
                  options={mealConditions}
                  value={mealCondition}
                  onChange={handleChangeMealCondition}
                />
              </div>
            </div>
            <div className="searchModalFormItem vertical">
              <div className="searchModalFormTitle">キーワード</div>
              <div className="searchModalFormInput">
                <input
                  className="input light"
                  value={keyword}
                  onChange={handleChangeKeyword}
                  placeholder="キーワード・施設名など"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="searchModalContent">
          <LineTitle text="こだわり条件" />
          <div className="searchModalForm">
            {planConditions && planConditions.map(({ id, name, tags }) => (
              <div key={id} className="searchModalFormItem vertical">
                <div className="searchModalFormTitle">{name}</div>
                <div className="searchModalFormInput">
                  {tags.map(({ id: tagId, name }) => (
                    <CheckBox key={tagId}
                      name={`searchBoxModal-${tagId}`}
                      id={`searchBoxModal-${tagId}`}
                      checked={tagIds.includes(tagId)}
                      onChange={() => dispatch(setTagId(tagId))}
                    >
                      {name}
                    </CheckBox>
                  ))}
                  {!hideSearchCondition && searchConditionsList.map(({ name, param }) => (
                    <CheckBox key={param}
                      name={`searchBoxModal-${param}`}
                      id={`searchBoxModal-${param}`}
                      checked={searchCondition.includes(param)}
                      onChange={() => dispatch(setSearchCondition(param))}
                    >
                      {name}
                    </CheckBox>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {!isHide && (<div className="searchModalForm">
            {otherConditions && otherConditions.map(({ id, name, tags }) => {
              // 「支払方法」グループのタグは表示しない
              if (name === '支払方法') return null;

              return (
                <div key={id} className="searchModalFormItem vertical">
                  <div className="searchModalFormTitle">{name}</div>
                  <div className="searchModalFormInput">
                    {tags.map(({ id: tagId, name }) => (
                      <CheckBox key={tagId}
                        name={`searchBoxModal-${tagId}`}
                        id={`searchBoxModal-${tagId}`}
                        checked={tagIds.includes(tagId)}
                        onChange={() => dispatch(setTagId(tagId))}
                      >
                        {name}
                      </CheckBox>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
        <div className="actionWrapper">
          <div className="action" onClick={() => setIsHide(!isHide)}>
            {isHide ? (
              <div className="openIcon">こだわり条件を追加する</div>
            ) : (
              <div className="closeIcon">閉じる</div>
            )}
          </div>
        </div>
        <div className="action">
          <div className="research">
            <LinkButton onClick={handleSearch} >
              変更する
            </LinkButton>
          </div>
          <div className="close"><LinkButton buttonType="navigateMin" onClick={onClose}>閉じる</LinkButton></div>
        </div>
      </div>
    </Modal>
  );
};

ModalAccommodationConditions.defaultProps = {
};

ModalAccommodationConditions.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onCancel: PropTypes.func,
  onClick: PropTypes.func,
  handleSearch: PropTypes.func.isRequired,
  hideRoom: PropTypes.bool,
  hideDestination: PropTypes.bool,
  maxStayNights: PropTypes.number,
  hideSearchCondition: PropTypes.bool,
  excludeLanding: PropTypes.bool,
  isPlanList: PropTypes.bool,
};

export default ModalAccommodationConditions;
